import { capitalize } from 'radash';

const ACRONYMS = new Set([
  'ad',
  'api',
  'aws',
  'csrf',
  'cve',
  'http',
  'https',
  'idor',
  'id',
  'jwt',
  'lfi',
  'rce',
  'rfi',
  'sql',
  'ssrf',
  'ssti',
  'url',
  'uri',
  'xss',
  'xxe',
]);

const SPECIAL_WORDS: Record<string, string> = {
  graphql: 'GraphQL',
  nosql: 'NoSQL',
  oauth: 'OAuth',
};

const DATE_LABEL_PREFIX =
  /^(week of |jan |feb |mar |apr |may |jun |jul |aug |sep |oct |nov |dec )/i;
const OBJECT_ID_HEX = /^[a-f0-9]{24}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SEED_PREFIX = /^\[[^\]]+\]\s*/;
const FINDING_SUFFIX = /\s+finding$/i;
const ENUM_TOKEN = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/g;

function formatWord(word: string): string {
  const lower = word.toLowerCase();
  const special = SPECIAL_WORDS[lower];

  if (special) {
    return special;
  }

  if (ACRONYMS.has(lower)) {
    return lower.toUpperCase();
  }

  return capitalize(lower);
}

function isLiteralValue(value: string): boolean {
  return (
    DATE_LABEL_PREFIX.test(value) ||
    value.startsWith('/') ||
    /^https?:\/\//i.test(value) ||
    OBJECT_ID_HEX.test(value) ||
    UUID.test(value)
  );
}

export function formatDataQueryLabel(value: string): string {
  const trimmed = value.trim();

  if (!trimmed || isLiteralValue(trimmed)) {
    return trimmed;
  }

  return trimmed
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(formatWord)
    .join(' ');
}

export function formatDataQueryTitle(value: string): string {
  const hadSeedPrefix = SEED_PREFIX.test(value);
  const withoutSeed = value.replace(SEED_PREFIX, '').trim();
  const stripped = (
    hadSeedPrefix ? withoutSeed.replace(FINDING_SUFFIX, '') : withoutSeed
  ).trim();

  if (!stripped) {
    return formatDataQueryLabel(value);
  }

  if (isLiteralValue(stripped) || !/[a-z]/.test(stripped)) {
    return formatDataQueryLabel(stripped);
  }

  return stripped.replace(ENUM_TOKEN, (token) => formatDataQueryLabel(token));
}

export function formatDataQueryFieldName(field: string): string {
  return formatDataQueryLabel(field.replace(/Id$/, ''));
}
